import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import {
  IconArrowLeft,
  IconDroplet,
  IconAlertTriangle,
  IconCheck,
} from '@tabler/icons-react';

export default function DervDetail() {
  const { tankId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile } = useAuth();
  const [tank, setTank] = useState(null);
  const [fuelLogs, setFuelLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const branchId = userProfile?.branchId;
  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId) {
        setLoading(false);
        return;
      }
      try {
        // Try branch-level tank first
        if (branchId) {
          const snap = await getDoc(
            doc(db, 'organisations', resolvedOrgId, 'branches', branchId, 'dervTanks', tankId)
          );
          if (snap.exists()) {
            setTank({ id: snap.id, branchName: branchId, ...snap.data() });

            // Load fuel logs
            const logsSnap = await getDocs(
              collection(db, 'organisations', resolvedOrgId, 'branches', branchId, 'dervTanks', tankId, 'fuelLogs')
            );
            setFuelLogs(logsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
            return;
          }
        }

        // Fall back to searching all branches for this tank
        const branchesSnap = await getDocs(
          collection(db, 'organisations', resolvedOrgId, 'branches')
        );
        for (const branchDoc of branchesSnap.docs) {
          const snap = await getDoc(
            doc(db, 'organisations', resolvedOrgId, 'branches', branchDoc.id, 'dervTanks', tankId)
          );
          if (snap.exists()) {
            setTank({ id: snap.id, branchName: branchDoc.data().name || branchDoc.id, ...snap.data() });

            const logsSnap = await getDocs(
              collection(db, 'organisations', resolvedOrgId, 'branches', branchDoc.id, 'dervTanks', tankId, 'fuelLogs')
            );
            setFuelLogs(logsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            break;
          }
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [tankId, resolvedOrgId, branchId]);

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
    </Layout>
  );

  if (!tank) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/fuel')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Fuel
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Tank not found</div>
      </div>
    </Layout>
  );

  const pct = Math.round((tank.currentLitres / tank.capacityLitres) * 100);
  const isLow = pct <= (tank.orderThresholdPct || 15);
  const isCritical = pct <= 10;

  const levelColour = isCritical ? 'var(--color-danger)' : isLow ? '#E8610A' : 'var(--color-success)';
  const levelBg = isCritical ? 'var(--color-danger-light)' : isLow ? '#FDF0E8' : 'var(--color-success-light)';
  const daysRemaining = Math.round(tank.currentLitres / 120);

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/fuel')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Fuel
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>{tank.name}</h1>
            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {tank.branchName} · {tank.capacityLitres?.toLocaleString()}L capacity
            </p>
          </div>
          <div style={{ fontSize: 11, padding: '4px 12px', borderRadius: 20, background: levelBg, color: levelColour, display: 'flex', alignItems: 'center', gap: 5 }}>
            {isCritical || isLow
              ? <IconAlertTriangle size={12} stroke={1.5} />
              : <IconCheck size={12} stroke={1.5} />
            }
            {isCritical ? 'Critical' : isLow ? 'Order required' : 'OK'}
          </div>
        </div>

        {isLow && (
          <div style={{ background: levelBg, border: `0.5px solid ${levelColour}`, borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, fontSize: 13, color: levelColour }}>
            <IconAlertTriangle size={18} stroke={1.5} />
            Tank at {pct}% — {isCritical ? 'critically low, order immediately' : 'below order threshold, auto-order email will be sent'}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '20px' }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>Current level</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20 }}>
              <div style={{ position: 'relative', width: 60, height: 140, background: 'var(--color-bg-secondary)', borderRadius: 8, overflow: 'hidden', border: '0.5px solid var(--color-border-light)', flexShrink: 0 }}>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${pct}%`, background: levelColour, borderRadius: '0 0 6px 6px', transition: 'height 0.5s ease' }} />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 500, color: pct > 30 ? '#fff' : levelColour }}>{pct}%</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 28, fontWeight: 400, color: levelColour, marginBottom: 4 }}>{tank.currentLitres?.toLocaleString()}L</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 12 }}>of {tank.capacityLitres?.toLocaleString()}L capacity</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>~{daysRemaining} days remaining</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Order threshold: {tank.orderThresholdPct}%</div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <div style={{ height: 8, background: 'var(--color-bg-secondary)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: levelColour, borderRadius: 4, transition: 'width 0.5s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                <span>0L</span>
                <span>{tank.capacityLitres?.toLocaleString()}L</span>
              </div>
            </div>
          </div>

          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Tank details</div>
            {[
              tank.lastFilled && { label: 'Last filled', value: tank.lastFilled },
              tank.lastFilledLitres && { label: 'Last fill volume', value: `${tank.lastFilledLitres?.toLocaleString()}L` },
              { label: 'Branch', value: tank.branchName },
              { label: 'Order threshold', value: `${tank.orderThresholdPct}%` },
              { label: 'Auto-order email', value: tank.orderEmailSent ? 'Sent' : 'Not sent' },
            ].filter(Boolean).map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '0.5px solid var(--color-border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 14, fontWeight: 400 }}>Fuel log</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{fuelLogs.length} entries</div>
          </div>

          {fuelLogs.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              No fuel logs yet — logs will appear here as staff fill up
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px 100px', padding: '8px 20px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
                {['Date', 'Driver', 'Asset', 'Litres', 'Meter reading'].map(h => (
                  <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
                ))}
              </div>
              {fuelLogs.map((log, i) => (
                <div key={log.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px 100px', padding: '12px 20px', borderBottom: i < fuelLogs.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', alignItems: 'center' }}>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{log.date || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-primary)' }}>{log.driverName || '—'}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, background: '#FAC775', color: '#412402', padding: '1px 6px', borderRadius: 3, display: 'inline-block' }}>{log.assetReg || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <IconDroplet size={12} color={levelColour} stroke={1.5} />
                    {log.litres}L
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-secondary)' }}>{log.meterReading || '—'}</div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}